import { ChevronsLeft } from "lucide-react";
import { NavLink } from "react-router-dom";

import { Button } from "@/shared/ui/button/Button";
import { cn } from "@/shared/lib/cn";
import type { Source } from "@/types";

import { getVisibleNavItems, type MenuVisibility } from "./nav";
import { useSidebarToggle } from "./SidebarToggleContext";

interface SidebarProps extends MenuVisibility {
  scope?: Source;
  /** Agent seat — unlocks the commission nav regardless of scope (dev parity). */
  isAgent?: boolean;
  /** While false the rail stays empty rather than guessing at the menu. */
  menuVisibilityKnown?: boolean;
  /** Called after a nav item is clicked (used to close the mobile drawer). */
  onNavigate?: () => void;
  /** Show the collapse handle (hidden inside the mobile drawer). */
  showCollapse?: boolean;
}

/**
 * Primary navigation rail — codonix "PLX new-version" design: a collapse
 * handle that slides the whole rail off-screen (the AppShell grid animates
 * the column to 0px and shows a floating re-opener), and nav links styled
 * `bg-primary/10 text-primary` when active. Nav items + scope gating come
 * from `nav.ts` (unchanged).
 */
export function Sidebar({
  scope,
  isAgent,
  visibleMenuIds,
  manageableLevels,
  menuVisibilityKnown,
  onNavigate,
  showCollapse = true,
}: SidebarProps) {
  const { setCollapsed } = useSidebarToggle();
  const items = getVisibleNavItems(scope, {
    isAgent,
    visibleMenuIds,
    manageableLevels,
    menuVisibilityKnown,
  });

  return (
    <nav className="flex h-full flex-col p-3" aria-label="Main navigation">
      {showCollapse && (
        <div className="mb-2 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            className="h-7 w-7 text-muted-foreground"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-1">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.id}
              to={item.href}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
